import { Node, mergeAttributes } from "@tiptap/react";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Extension } from "@tiptap/core";
import { Bold } from "@tiptap/extension-bold";
import HardBreak from "@tiptap/extension-hard-break";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import TableCell from "@tiptap/extension-table-cell";
import Underline from "@tiptap/extension-underline";

const notWriteOnImagePlugin = new Plugin({
  key: new PluginKey("notWriteOnImagePlugin"),
  props: {
    handleTextInput(view, _from, _to, text) {
      const { state, dispatch } = view;
      const { selection } = state;
      const { $from } = selection;

      if (selection?.node?.type?.name === "image") {
        const { tr } = state;
        tr.insert($from.pos + 1, state.schema.text(text));
        tr.setSelection(
          TextSelection.create(tr.doc, $from.pos + 1 + text.length)
        );
        dispatch(tr);
        return true;
      }

      return false;
    },
  },
});

export const CustomBold = Bold.extend({
  addAttributes() {
    return {
      style: {
        renderHTML: (attributes) => attributes.style,
      },
    };
  },
});

export const CustomHardBreak = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      "Shift-Enter": () => this.editor.commands.setHardBreak(),
    };
  },
});

export const CustomHighlight = Highlight.extend({
  parseHTML() {
    return [
      { tag: "mark" },
      {
        tag: "span",
        getAttrs: (element) =>
          Boolean(element.style?.backgroundColor) &&
          element.nodeName === "SPAN",
      },
    ];
  },

  addAttributes() {
    if (!this.options.multicolor) {
      return {};
    }

    return {
      color: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-color") || element.style.backgroundColor,
        renderHTML: (attributes) => {
          if (!attributes.color) {
            return {};
          }

          return {
            "data-color": attributes.color,
            style: `background-color: ${attributes.color}; display: inline-flex;`,
          };
        },
      },
      fontColor: {
        default: null,
        parseHTML: (element) => element.style.color,
        renderHTML: (attributes) => {
          if (!attributes.fontColor) {
            return {};
          }

          return {
            style: `color: ${attributes.fontColor ?? "inherit"};`,
          };
        },
      },
    };
  },
});

export const CustomImageResize = Image.extend({
  draggable: true,
  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      style: {
        default: "cursor: pointer;",
      },
      height: {
        renderHTML: (attr) => ({ height: attr.height ?? "auto" }),
      },
      width: {
        renderHTML: (attr) => ({ width: attr.width ?? "auto" }),
      },
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const { view } = editor;

      const createImageView = () => {
        const dom = document.createElement("div");
        dom.classList.add("node-view");
        dom.style.display = "inline-flex";
        dom.style.position = "relative";
        dom.style.width = "fit-content";

        const borderContainer = document.createElement("div");
        borderContainer.style.position = "absolute";
        borderContainer.style.top = "0";
        borderContainer.style.left = "0";
        borderContainer.style.right = "0";
        borderContainer.style.bottom = "0";
        borderContainer.style.border = "1px dashed transparent";
        borderContainer.style.zIndex = "31414";
        borderContainer.style.pointerEvents = "none";

        const img = document.createElement("img");
        img.src = node.attrs.src;
        img.alt = node.attrs.alt ?? "image";
        img.style.cssText = node.attrs.style;
        img.style.verticalAlign = "bottom";
        img.style.cursor = "pointer";
        img.style.maxHeight = "170px";

        img.onload = () => {
          const aspectRatio = img.naturalHeight / img.naturalWidth;

          if (img.style.height && !img.style.width) {
            if (img.style.height.includes("%")) {
              const heightInPx =
                img.naturalHeight * (img.style.height.split("%")[0] / 100);
              img.style.width = `${heightInPx * aspectRatio}px`;
              img.style.height = `${heightInPx}px`;
            } else {
              img.style.width = `${
                img.style.height.split(px)[0] * aspectRatio
              }px`;
            }
          }

          if (!img.style.height && img.style.width) {
            if (img.style.width.includes("%")) {
              const widthInPx =
                img.naturalWidth * (img.style.width.split("%")[0] / 100);
              img.style.height = `${widthInPx * aspectRatio}px`;
              img.style.width = `${widthInPx}px`;
            } else {
              img.style.height = `${
                img.style.width.split(px)[0] * aspectRatio
              }px`;
            }
          }

          if (!img.style.height && !img.style.width) {
            if (node.attrs.height) {
              img.style.height = `${node.attrs.height}px`;
            } else {
              img.style.height = img.naturalHeight > 170 ? "170px" : "auto";
            }

            img.style.width = node.attrs.width
              ? `${node.attrs.width}px`
              : "auto";
          }

          img.style.maxHeight = null;
        };

        img.addEventListener("click", () => {
          if (!dom.classList.contains("edit-mode")) {
            dom.classList.add("edit-mode");
          }
        });

        dom.appendChild(borderContainer);
        dom.appendChild(img);

        return { dom, img, borderContainer };
      };

      const createResizeDots = (dom, img) => {
        const position = [
          { top: "-3px", right: null, bottom: null, left: "-3px" },
          { top: "-3px", right: "-3px", bottom: null, left: null },
          { top: null, right: "-3px", bottom: "-3px", left: null },
          { top: null, right: null, bottom: "-3px", left: "-3px" },
        ];

        const dots = [];

        for (let i = 0; i < 4; i++) {
          const dot = document.createElement("div");
          dot.style.backgroundColor = "var(--colors-brand-secondary-800)";
          dot.style.position = "absolute";
          dot.style.borderRadius = "50%";
          dot.style.height = "5px";
          dot.style.width = "5px";
          dot.style.zIndex = "31415";
          dot.style.cursor = "nwse-resize";
          dot.style.display = "none";

          dot.style.top = position[i].top;
          dot.style.right = position[i].right;
          dot.style.bottom = position[i].bottom;
          dot.style.left = position[i].left;

          const onMouseDown = (e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const startWidth = parseInt(
              document.defaultView.getComputedStyle(img).width,
              10
            );
            const startHeight = parseInt(
              document.defaultView.getComputedStyle(img).height,
              10
            );

            const onMouseMove = (event) => {
              let newWidth = startWidth + (event.clientX - startX);
              let newHeight = startHeight + (event.clientY - startY);

              if (i === 0 || i === 3) {
                newWidth = startWidth - (event.clientX - startX);
                newHeight = startHeight - (event.clientY - startY);
              }

              const aspectRatio = startWidth / startHeight;
              if (newWidth / newHeight > aspectRatio) {
                newHeight = newWidth / aspectRatio;
              } else {
                newWidth = newHeight * aspectRatio;
              }

              img.style.width = `${newWidth}px`;
              img.style.height = `${newHeight}px`;
              dom.style.width = `${newWidth}px`;
              dom.style.height = `${newHeight}px`;
            };

            const onMouseUp = () => {
              document.removeEventListener("mousemove", onMouseMove);
              document.removeEventListener("mouseup", onMouseUp);

              if (typeof getPos === "function") {
                const { width, height, ...rest } = node.attrs;
                view.dispatch(
                  view.state.tr.setNodeMarkup(getPos(), undefined, {
                    ...rest,
                    width,
                    height,
                  })
                );
                editor.commands.focus();
              }
            };

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
          };

          dot.addEventListener("mousedown", onMouseDown);

          dom.append(dot);
          dots.push(dot);
        }

        return dots;
      };

      const { dom, img, borderContainer } = createImageView();
      const dots = createResizeDots(dom, img);

      const handleClickToggleEdition = (e) => {
        if (!dom.contains(e.target)) {
          dom.classList.remove("edit-mode");

          dots.forEach((dot) => {
            dot.style.display = "none";
          });
          borderContainer.style.borderColor = "transparent";
        } else {
          dom.classList.add("edit-mode");

          dots.forEach((dot) => {
            dot.style.display = "block";
          });
          borderContainer.style.borderColor = "var(--colors-brand-secondary-800)";
        }
      };

      const handleBlur = (e) => {
        if (!dom.contains(e.target)) {
          dom.classList.remove("edit-mode");
          dots.forEach((dot) => {
            dot.style.display = "none";
          });
          borderContainer.style.borderColor = "transparent";
        } else {
          dom.classList.add("edit-mode");
          dots.forEach((dot) => {
            dot.style.display = "block";
          });
          borderContainer.style.borderColor = "var(--colors-brand-secondary-800)";
        }
      };

      document.addEventListener("click", handleClickToggleEdition);
      document.addEventListener("keydown", handleBlur);

      const destroy = () => {
        document.removeEventListener("click", handleClickToggleEdition);
        document.removeEventListener("keydown", handleBlur);
      };

      return { dom, destroy };
    };
  },

  addProseMirrorPlugins() {
    return [notWriteOnImagePlugin];
  },
});

export const CustomLineHeight = Extension.create({
  name: "lineHeight",

  addOptions() {
    return {
      types: [],
      defaultLineHeight: "1.5",
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: this.options.defaultLineHeight,
            parseHTML: (element) =>
              element.style.lineHeight || this.options.defaultLineHeight,
            renderHTML: (attributes) => {
              if (attributes.lineHeight === this.options.defaultLineHeight) {
                return {};
              }

              return { style: `line-height: ${attributes.lineHeight}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (alignment) =>
        ({ commands }) =>
          this.options.types.every((type) =>
            commands.updateAttributes(type, { lineHeight: alignment })
          ),
    };
  },
});

export const CustomLink = Link.extend({
  addAttributes() {
    return {
      href: {
        default: null,
      },
      target: {
        default: this.options.HTMLAttributes.target,
      },
      rel: {
        default: this.options.HTMLAttributes.rel,
      },
      class: {
        default: this.options.HTMLAttributes.class,
      },
      style: {
        renderHTML: (attributes) => ({
          style: `display: inline-flex; ${attributes.style || ""}`,
        }),
      },
    };
  },
});

export const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      style: {
        renderHTML: (attributes) => {
          const STYLES_DUPLICATED = [
            "margin",
            "text-align",
            "font-weight",
            "line-height",
          ];

          let updatedStyles = attributes.style || "";

          STYLES_DUPLICATED.forEach((property) => {
            const regex = new RegExp(`${property}:\\s*[^;]+;`, "g");
            updatedStyles = updatedStyles.replaceAll(regex, "");
          });

          updatedStyles = updatedStyles.trim();
          updatedStyles = `${updatedStyles} margin: 0 0 10px;`;

          return { style: updatedStyles };
        },
      },
    };
  },
});

export const CustomTable = Node.create({
  name: "table",
  content: "tableRow+",

  tableRole: "table",

  isolating: true,

  group: "block",

  parseHTML() {
    return [{ tag: "table" }];
  },
  renderHTML({ HTMLAttributes }) {
    const table = [
      "table",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];

    return table;
  },
});

export const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      style: {
        renderHTML: (attributes) => ({ style: attributes.style }),
      },
    };
  },
});

export const CustomUnderline = Underline.extend({
  parseHTML() {
    return [
      {
        tag: "u",
      },
      {
        style: "text-decoration",
        consuming: false,
        getAttrs: (style) => (style.includes("underline") ? {} : false),
      },
      {
        style: "text-decoration-line",
        getAttrs: (style) => (style.includes("underline") ? {} : false),
      },
    ];
  },

  addAttributes() {
    return {
      style: {
        renderHTML: (attributes) => ({
          style: `${attributes.style} text-decoration: underline;`,
        }),
      },
    };
  },
});
